// src/lib/analytics.ts
import posthog from "posthog-js";
import { isPublicAnalyticsEnabled } from "@/lib/feature-flags";
// Módulo liviano a propósito (FEATURE-205 RNF-01): este archivo lo carga el layout de
// `[locale]`, o sea TODAS las páginas. Importar el slug desde `leagues/href` traería
// `LeagueNav` y los feature flags al bundle de marketing, login, admin y acta.
import { slugFromPath } from "@/lib/league-slug";

let initialized = false;

const INITIAL_UTM_FLAG = "ld_initial_utm_set";
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

export function initPostHog(): void {
  if (initialized) return;

  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!key) return;

  posthog.init(key, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com",
    person_profiles: "identified_only",
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: true,
    disable_session_recording: true,
    persistence: "localStorage",
    loaded: (ph) => {
      if (process.env.NODE_ENV === "development") ph.debug();
    },
  });

  initialized = true;
}

/**
 * Captura un evento genérico. Punto de extensión para los eventos de dominio
 * (registro de ligas, creación de equipos, etc.) cuando existan.
 */
export function capture(event: string, properties?: Record<string, unknown>): void {
  if (!initialized) return;
  posthog.capture(event, properties);
}

// --- Alcance del evento: de qué liga y de qué pantalla viene (FEATURE-205) ---

/**
 * Superficie del producto, en el vocabulario de ADR-007 §2. `association` es la **portada**
 * de la liga; no confundir con la super property `association`, que es el slug: una dice qué
 * pantalla y la otra de qué liga.
 */
export type Surface = "fan" | "association" | "admin" | "scoresheet" | "marketing";

/** Deriva la superficie del pathname (FEATURE-205 §7.2). Sin `window`, `marketing`. */
export function surfaceFromPath(): Surface {
  if (typeof window === "undefined") return "marketing";

  // Forma corta `/<slug>` (producción): la CloudFront Function de FEATURE-002 la reescribe
  // SIEMPRE a la portada, sin mirar el query, así que acá no hay ambigüedad que resolver.
  if (slugFromPath()) return "association";

  switch (window.location.pathname.split("/")[2] ?? "") {
    case "leagues":
      return "fan";
    case "associations":
      return "association";
    case "app":
      return "admin";
    case "scoresheet":
      return "scoresheet";
    default:
      return "marketing";
  }
}

/**
 * Registra `association` + `surface` como super properties, para que **todo** evento
 * posterior —pageview, web vitals, y los de dominio que vengan— salga etiquetado sin tener
 * que tocarlos uno por uno.
 *
 * La asociación sale de la URL con una sola regla para las tres superficies que la llevan:
 * el path en producción, el `?league=` del hincha y la portada, y el `?association=` del
 * alcance del organizador. **Es el mismo slug en todas** (FEATURE-205 §7.3).
 *
 * Se lee de `window.location`, no del estado de React: cuando corre el efecto del pageview
 * la URL ya es la definitiva, así que no hay carrera con los stores de ubicación.
 *
 * El `unregister` no es un detalle: las super properties persisten en `localStorage`, así
 * que sin él la liga anterior quedaría pegada al salir a la landing (CB-001).
 */
export function syncEventScope(): void {
  if (!initialized) return;
  if (typeof window === "undefined") return;

  const params = new URLSearchParams(window.location.search);
  const association = slugFromPath() ?? params.get("league") ?? params.get("association");

  posthog.register({ surface: surfaceFromPath() });

  if (association) posthog.register({ association });
  else posthog.unregister("association");
}

// --- Eventos públicos ---
// Gated por `isPublicAnalyticsEnabled` (NEXT_PUBLIC_ENABLE_PUBLIC_ANALYTICS).

export function trackPageview(url: string): void {
  if (!isPublicAnalyticsEnabled) return;
  capture("$pageview", { $current_url: url });
}

// --- First-touch UTM attribution ---

/**
 * Superficies que navega un **visitante anónimo**. Ahí la atribución va como propiedad del
 * evento y NO de la persona (FEATURE-205 RF-07): `setPersonProperties` crea el perfil aunque
 * el visitante nunca se loguee —contradiciendo el `person_profiles: "identified_only"` de
 * arriba—, y esos eventos pasan a contarse como identificados, que cuestan ~4×. El hincha es
 * justo el tráfico de volumen: es lo que se comparte por WhatsApp.
 */
const ANONYMOUS_SURFACES: Surface[] = ["fan", "association"];

/**
 * Persiste utm_* + referrer del primer pageview de la sesión.
 *
 * **Dónde aterrizan depende de la superficie** (FEATURE-205 RF-07/RF-08):
 * - Hincha y portada → propiedades del **evento** (`register`), sin crear perfil.
 * - Marketing, auth, admin y acta → propiedades de la **persona**, como siempre: es el camino
 *   por el que llega y se registra un organizador, y ahí la atribución first-touch sobrevive
 *   al `identify()` porque PostHog mergea las propiedades anónimas al perfil identificado.
 *
 * El guard de `sessionStorage` es **por pestaña** (verificado en navegador): sobrevive a
 * navegaciones duras y recargas, así que esto corre una vez por pestaña.
 */
export function captureInitialUtm(searchParams: URLSearchParams): void {
  if (!isPublicAnalyticsEnabled) return;
  if (!initialized) return;
  if (typeof window === "undefined") return;
  if (window.sessionStorage.getItem(INITIAL_UTM_FLAG)) return;

  const initialProps: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const value = searchParams.get(key);
    if (value) initialProps[`initial_${key}`] = value;
  }
  if (typeof document !== "undefined" && document.referrer) {
    initialProps.initial_referrer = document.referrer;
  }

  window.sessionStorage.setItem(INITIAL_UTM_FLAG, "1");

  if (Object.keys(initialProps).length === 0) return;

  if (ANONYMOUS_SURFACES.includes(surfaceFromPath())) posthog.register(initialProps);
  else posthog.setPersonProperties(undefined, initialProps);
}

// --- Identificación ---

export function identifyUser(
  userId: string,
  traits: { email: string; name: string }
): void {
  if (!initialized) return;
  posthog.identify(userId, traits);
}

export function resetUser(): void {
  if (!initialized) return;
  posthog.reset();
}
